import { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, trackEvent } from "@/lib/api";

const reveal = {
  initial: { opacity: 0, y: 48 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const inputCls =
  "w-full bg-transparent border-b border-charcoal/20 py-4 text-base md:text-lg text-charcoal placeholder:text-charcoal/30 focus:outline-none focus:border-vermillion transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    trackEvent("send_message_click", { button_name: "Send Message" });
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message has been received.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-ivory border-t border-charcoal/10 py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <motion.p {...reveal} className="text-xs uppercase tracking-[0.35em] text-gold font-medium">
            Contact
          </motion.p>
          <motion.h2 {...reveal} className="mt-6 font-serif font-medium tracking-tight text-charcoal text-4xl md:text-6xl leading-[1.05]">
            Write to us.
          </motion.h2>
          <motion.p {...reveal} className="mt-8 text-charcoal/65 text-base md:text-lg leading-relaxed max-w-md">
            For enquiries, collaborations, corrections or cultural contributions — every message is read with care.
          </motion.p>
        </div>

        <motion.form {...reveal} onSubmit={submit} className="lg:col-span-7" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
            <div>
              <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.25em] text-charcoal/50 font-medium">
                Name
              </label>
              <input
                id="contact-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                className={inputCls}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.25em] text-charcoal/50 font-medium">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className={inputCls}
                data-testid="contact-email-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.25em] text-charcoal/50 font-medium">
                Message
              </label>
              <textarea
                id="contact-message"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what you have in mind…"
                className={`${inputCls} resize-none`}
                data-testid="contact-message-input"
              />
            </div>
          </div>
          <motion.button
            type="submit"
            disabled={sending}
            whileTap={{ scale: 0.96 }}
            data-testid="contact-submit-button"
            className="group mt-12 inline-flex items-center gap-3 bg-charcoal text-ivory text-xs uppercase tracking-[0.22em] font-medium px-10 py-5 transition-colors duration-300 hover:bg-vermillion disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Send Message"}
            <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
}
